import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { ShieldCheck, FileText, Check, Building2, Clock, ArrowRight, Upload, X, Loader2, Heart } from "lucide-react";
import PhotoUpload from "../components/PhotoUpload";
import PayButton from "../components/PayButton";

// 5-stage labels used both here (parent timeline) and on admin page.
const STAGE_LABELS = {
  awaiting_payment:       "Awaiting payment",
  submitted:              "Submitted",
  documents_verified:     "Documents verified",
  submitted_to_authority: "Filed at authority",
  certificate_received:   "Certificate received",
  delivered:              "Delivered",
};
const STAGE_ORDER = ["submitted", "documents_verified", "submitted_to_authority", "certificate_received", "delivered"];

export default function PetRegistration() {
  const { user } = useAuth();
  const [cities, setCities] = useState([]);
  const [mine, setMine] = useState([]);
  const [pets, setPets] = useState([]);
  const [picking, setPicking] = useState(null); // city object selected
  const [draft, setDraft] = useState(null);     // server-side draft once we POST

  useEffect(() => {
    axios.get(`${API}/pet-registration/cities`).then(r => setCities(r.data.cities || []));
    axios.get(`${API}/pet-registration/mine`).then(r => setMine(r.data.items || []));
    axios.get(`${API}/pets`).then(r => setPets(r.data || [])).catch(() => {});
  }, []);

  const refreshMine = () => axios.get(`${API}/pet-registration/mine`).then(r => setMine(r.data.items || []));

  return (
    <div className="space-y-8" data-testid="pet-registration-page">
      {/* Header */}
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 bg-leaf/40 border border-leaf rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider">
            <ShieldCheck className="w-3 h-3 text-brand"/> New · concierge service
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-extrabold mt-3">Pet registration, done for you.</h1>
          <p className="text-ink-soft mt-2">
            Municipal pet licences are mandatory but the paperwork is painful. Pick your city, upload your documents,
            pay once — PetBond's ops team files with the authority and delivers your certificate. <Link to="/terms" className="underline">Terms</Link>.
          </p>
        </div>
      </header>

      {/* My existing requests */}
      {mine.length > 0 && (
        <section className="space-y-3" data-testid="my-registrations">
          <h2 className="font-heading text-xl font-extrabold">Your requests</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {mine.map(r => <RequestCard key={r.request_id} r={r} onChange={refreshMine}/>)}
          </div>
        </section>
      )}

      {/* City picker */}
      <section className="space-y-3">
        <h2 className="font-heading text-xl font-extrabold">Choose your city</h2>
        <p className="text-sm text-ink-soft">
          We currently support 6 metros. <span className="font-bold text-ink">Other cities — coming soon.</span>
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {cities.map(c => (
            <button
              key={c.city_id}
              data-testid={`city-${c.city_id}`}
              onClick={() => setPicking(c)}
              className="text-left bg-surface border border-line rounded-3xl p-5 shadow-soft hover:-translate-y-1 hover:shadow-hover transition-all"
            >
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-2xl bg-brand/10 text-brand grid place-items-center shrink-0"><Building2 className="w-5 h-5"/></div>
                <div className="flex-1">
                  <div className="font-heading text-lg font-extrabold">{c.city}</div>
                  <div className="text-xs text-ink-soft mt-0.5">{c.authority}</div>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                <div className="bg-surface-muted rounded-xl p-2">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-ink-soft">Govt fee</div>
                  <div className="font-heading text-base font-extrabold mt-0.5">₹{c.govt_fee}</div>
                </div>
                <div className="bg-surface-muted rounded-xl p-2">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-ink-soft">Service</div>
                  <div className="font-heading text-base font-extrabold mt-0.5">₹{c.service_fee}</div>
                </div>
                <div className="bg-brand/10 text-brand rounded-xl p-2">
                  <div className="text-[10px] font-bold uppercase tracking-wider opacity-70">In</div>
                  <div className="font-heading text-base font-extrabold mt-0.5">{c.sla_days}d</div>
                </div>
              </div>
              <div className="mt-3 text-xs font-bold text-brand inline-flex items-center gap-1">
                Start request <ArrowRight className="w-3.5 h-3.5"/>
              </div>
            </button>
          ))}
        </div>
      </section>

      {picking && !draft && (
        <RequestForm
          city={picking}
          pets={pets}
          onCancel={() => setPicking(null)}
          onCreated={(d) => { setDraft(d); refreshMine(); }}
        />
      )}
      {draft && (
        <PayStep
          draft={draft}
          onPaid={async () => {
            await axios.post(`${API}/pet-registration/requests/${draft.request_id}/paid`);
            setDraft(null);
            setPicking(null);
            await refreshMine();
          }}
          onClose={() => setDraft(null)}
        />
      )}
    </div>
  );
}

function RequestForm({ city, pets, onCancel, onCreated }) {
  const [form, setForm] = useState({
    pet_id: pets?.[0]?.pet_id || "",
    pet_name: pets?.[0]?.name || "",
    species: pets?.[0]?.species || "dog",
    breed: pets?.[0]?.breed || "",
    age_years: pets?.[0]?.age_years ?? 1,
    pet_color: "",
    gender: "male",
    owner_phone: "",
    owner_address: "",
    notes: "",
    documents: {},
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setDoc = (k, v) => setForm(f => ({ ...f, documents: { ...f.documents, [k]: v } }));

  const onPickPet = (petId) => {
    const p = pets.find(x => x.pet_id === petId);
    if (!p) return;
    setForm(f => ({ ...f, pet_id: p.pet_id, pet_name: p.name, species: p.species, breed: p.breed || "", age_years: p.age_years ?? 1 }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSaving(true);
    try {
      const r = await axios.post(`${API}/pet-registration/requests`, { city_id: city.city_id, ...form });
      onCreated(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not submit — please check the fields.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onCancel} data-testid="petreg-form-modal">
      <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-surface rounded-3xl p-6 w-full max-w-2xl max-h-[92vh] overflow-y-auto border border-line shadow-soft">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-heading text-xl font-extrabold">Pet registration — {city.city}</h3>
            <div className="text-xs text-ink-soft">{city.authority}</div>
          </div>
          <button type="button" onClick={onCancel}><X className="w-5 h-5"/></button>
        </div>

        <div className="bg-leaf/20 border border-leaf rounded-2xl px-3 py-2 text-xs text-ink-soft mb-4">{city.notes}</div>

        {pets.length > 0 && (
          <div className="mb-4">
            <div className="text-xs font-bold uppercase tracking-wider text-ink-soft mb-1.5">Pick a pet</div>
            <div className="flex flex-wrap gap-2">
              {pets.map(p => (
                <button
                  type="button"
                  key={p.pet_id}
                  data-testid={`petreg-pet-${p.pet_id}`}
                  onClick={() => onPickPet(p.pet_id)}
                  className={`rounded-full px-3 py-1.5 text-xs font-bold border ${form.pet_id === p.pet_id ? "bg-brand text-white border-brand" : "bg-surface border-line text-ink-soft hover:border-brand"}`}
                >
                  {p.name} · {p.breed}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Pet name *"><input data-testid="petreg-name" required value={form.pet_name} onChange={e => set("pet_name", e.target.value)} className="inp"/></Field>
          <Field label="Species">
            <select value={form.species} onChange={e => set("species", e.target.value)} className="inp">
              <option value="dog">Dog</option><option value="cat">Cat</option><option value="rabbit">Rabbit</option><option value="bird">Bird</option><option value="other">Other</option>
            </select>
          </Field>
          <Field label="Breed"><input value={form.breed} onChange={e => set("breed", e.target.value)} className="inp"/></Field>
          <Field label="Age (years)"><input type="number" step="0.5" min="0" value={form.age_years} onChange={e => set("age_years", Number(e.target.value))} className="inp"/></Field>
          <Field label="Colour"><input value={form.pet_color} onChange={e => set("pet_color", e.target.value)} className="inp" placeholder="Tan / Black / White"/></Field>
          <Field label="Gender">
            <select value={form.gender} onChange={e => set("gender", e.target.value)} className="inp">
              <option value="male">Male</option><option value="female">Female</option>
            </select>
          </Field>
          <Field label="Your phone *"><input data-testid="petreg-phone" required value={form.owner_phone} onChange={e => set("owner_phone", e.target.value)} className="inp" placeholder="+91-…"/></Field>
          <Field label="Full address (for authority filing) *">
            <input data-testid="petreg-address" required value={form.owner_address} onChange={e => set("owner_address", e.target.value)} className="inp"/>
          </Field>
        </div>

        <div className="mt-5">
          <div className="text-xs font-bold uppercase tracking-wider text-ink-soft mb-2">Required documents</div>
          <div className="space-y-3">
            {city.required_docs.map(d => (
              <DocUpload
                key={d.key}
                docKey={d.key}
                label={d.label + (d.key === "society_noc" ? " (optional)" : " *")}
                value={form.documents[d.key]}
                onChange={(v) => setDoc(d.key, v)}
              />
            ))}
          </div>
        </div>

        <Field label="Notes for our ops team (optional)">
          <textarea rows={2} value={form.notes} onChange={e => set("notes", e.target.value)} className="inp" placeholder="Any special instructions, society contact details, etc."/>
        </Field>

        {err && <div className="text-sm font-bold text-danger mt-3" data-testid="petreg-err">{err}</div>}

        <div className="mt-5 flex items-center justify-between bg-surface-muted rounded-2xl px-4 py-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-ink-soft">You'll pay next</div>
            <div className="font-heading text-xl font-extrabold mt-0.5">₹{city.govt_fee + city.service_fee}</div>
            <div className="text-[11px] text-ink-soft">Govt ₹{city.govt_fee} + PetBond ₹{city.service_fee}</div>
          </div>
          <button disabled={saving} data-testid="petreg-submit" className="bg-brand text-white rounded-full px-5 py-3 font-bold inline-flex items-center gap-2 hover:bg-brand-hover disabled:opacity-60">
            {saving ? <><Loader2 className="w-4 h-4 animate-spin"/> Saving…</> : <>Continue to pay <ArrowRight className="w-4 h-4"/></>}
          </button>
        </div>
        <style>{`.inp{width:100%;border:1px solid #EAE0D5;border-radius:14px;padding:0.55rem 0.9rem;background:#FFFDF7;font-family:'Nunito';font-weight:600;outline:none;}.inp:focus{border-color:#FF6B35;}`}</style>
      </form>
    </div>
  );
}

function PayStep({ draft, onPaid, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-surface rounded-3xl p-6 w-full max-w-md border border-line shadow-soft" data-testid="petreg-pay-modal">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-xl font-extrabold">Confirm & pay</h3>
          <button onClick={onClose}><X className="w-5 h-5"/></button>
        </div>
        <div className="space-y-2 text-sm">
          <Row k="Pet" v={`${draft.pet_name} · ${draft.species}`}/>
          <Row k="City" v={draft.city}/>
          <Row k="Authority" v={draft.authority}/>
          <Row k="SLA" v={`~ ${draft.sla_days} working days`}/>
          <div className="border-t border-line my-3"/>
          <Row k="Govt fee" v={`₹${draft.govt_fee}`}/>
          <Row k="PetBond service fee" v={`₹${draft.service_fee}`}/>
          <Row k="Total" v={`₹${draft.total_amount}`} bold/>
        </div>
        <div className="mt-5">
          <PayButton
            amount={draft.total_amount}
            currency="INR"
            country="IN"
            purpose="pet_registration"
            referenceId={draft.request_id}
            label={`Pay ₹${draft.total_amount}`}
            dataTestid="petreg-pay-btn"
            onSuccess={onPaid}
          />
        </div>
        <div className="text-[11px] text-ink-soft text-center mt-3">
          Service fee is non-refundable once filing begins. Govt fee refundable if rejected by authority.
        </div>
      </div>
    </div>
  );
}

function RequestCard({ r, onChange }) {
  const currentIdx = STAGE_ORDER.indexOf(r.status);
  const isAwaitingPay = r.status === "awaiting_payment";
  return (
    <article className="bg-surface border border-line rounded-3xl p-5 shadow-soft" data-testid={`petreg-card-${r.request_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-ink-soft">{r.city} · {r.authority.split(" — ")[0]}</div>
          <h4 className="font-heading text-lg font-extrabold mt-0.5">{r.pet_name}</h4>
          <div className="text-xs text-ink-soft">{r.species} · {r.breed || "—"} · {r.age_years} yrs</div>
        </div>
        <div className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2.5 py-1 ${isAwaitingPay ? "bg-sun text-ink" : r.status === "delivered" ? "bg-leaf text-ink" : "bg-brand/15 text-brand"}`}>
          {STAGE_LABELS[r.status] || r.status}
        </div>
      </div>

      {!isAwaitingPay && (
        <div className="mt-4">
          <div className="flex items-center gap-1 mb-2">
            {STAGE_ORDER.map((s, i) => (
              <div key={s} className={`h-1.5 flex-1 rounded-full ${i <= currentIdx ? "bg-brand" : "bg-line"}`} title={STAGE_LABELS[s]}/>
            ))}
          </div>
          <div className="grid grid-cols-5 gap-1 text-[9px] font-bold uppercase tracking-wider text-ink-soft text-center">
            {STAGE_ORDER.map((s, i) => (
              <div key={s} className={i <= currentIdx ? "text-brand" : ""}>{STAGE_LABELS[s].split(" ")[0]}</div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-4 flex items-center justify-between text-xs text-ink-soft">
        <div>Total ₹{r.total_amount} {r.paid_at && <span className="text-leaf font-bold ml-1">· paid</span>}</div>
        {r.certificate_url ? (
          <a href={r.certificate_url} target="_blank" rel="noreferrer" data-testid={`petreg-cert-${r.request_id}`} className="bg-brand text-white rounded-full px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1.5">
            <FileText className="w-3 h-3"/> Download certificate
          </a>
        ) : isAwaitingPay ? (
          <span className="text-sun font-bold">Awaiting payment</span>
        ) : (
          <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3"/> ETA {r.sla_days}d</span>
        )}
      </div>
    </article>
  );
}

function Row({ k, v, bold }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-ink-soft">{k}</span>
      <span className={bold ? "font-heading text-lg font-extrabold" : "font-bold"}>{v}</span>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block mt-3 col-span-2 sm:col-auto">
      <span className="text-xs font-bold uppercase tracking-wider text-ink-soft">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function DocUpload({ docKey, label, value, onChange }) {
  return (
    <div className="bg-surface-muted rounded-2xl p-3">
      <div className="flex items-center gap-2 mb-1.5">
        <div className="w-7 h-7 rounded-lg bg-brand/10 grid place-items-center text-brand"><Upload className="w-3.5 h-3.5"/></div>
        <div className="text-xs font-bold">{label}</div>
        {value && <Check className="w-3.5 h-3.5 text-leaf ml-auto"/>}
      </div>
      <PhotoUpload value={value} onChange={onChange} label="" testid={`petreg-doc-${docKey}`}/>
    </div>
  );
}
